import type React from 'react';
import { useState } from 'react';

import { useParams } from 'react-router-dom';

import CreateDoublesTeamsOverlay from 'components/overlays/doubles/CreateDoublesTeamsOverlay';
import { type PairedParticipantInterface } from 'components/overlays/doubles/types';
import { Feature, featureEnabled } from 'config/clients';
import { usePersonService } from 'hooks/person/usePersonService';
import { type ParticipantInterface, type ParticipantPairArray } from 'typedefs/store/tmxTypes';

interface CreateDoubleTeamsContainerProps {
  isOpen?: boolean;
  pairedDrawParticipants: ParticipantPairArray;
  pairedIndividualParticipants: ParticipantInterface[];
  unpairedDrawParticipants: ParticipantInterface[];
  closeDialog(): void;
  saveTeams: (participantIdPairs: Array<string[]>) => void;
}

const CreateDoubleTeamsContainer: React.FC<React.PropsWithChildren<CreateDoubleTeamsContainerProps>> = ({
  closeDialog,
  saveTeams,
  pairedDrawParticipants,
  unpairedDrawParticipants,
  pairedIndividualParticipants,
  isOpen,
}) => {
  const personService = usePersonService();
  const [pairedParticipantsLocal, setPairedParticipantsLocal] = useState<PairedParticipantInterface[]>();
  const { eventId } = useParams<{ eventId: string }>();
  const [{ combinedEligibilityLoading }, setGetCombinedEligibilityResult] = useState<{
    combinedEligibilityLoading?: boolean;
  }>({ combinedEligibilityLoading: false });

  const arraysMatch = function (arr1, arr2) {
    if (arr1.length !== arr2.length) return false;
    for (let i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) return false;
    }
    return true;
  };

  const handleGetCombinedEligibility = async (participantIdPairs: string[][]) => {
    // Currently enabling this feature means other combined eligibility checks via TS disabled
    // Instead we check only locally that the exact participant pair does not already exist
    if (featureEnabled(Feature.MULTI_PAIRED_PARTICIPANTS)) {
      handleLocalEligibilities();
      return;
    }

    setGetCombinedEligibilityResult({ combinedEligibilityLoading: true });
    const combinedEligibility = await personService.getPersonCombinedEventEligibility(
      participantIdPairs,
      eventId,
      'network-only',
    );
    setGetCombinedEligibilityResult({ combinedEligibilityLoading: false });
    if (combinedEligibility?.data) {
      const pairsEligibility = combinedEligibility.data?.combineParticipantsEligibility;
      const updatedPairs = pairedParticipantsLocal.map((pair) => {
        const pairEligibility = pairsEligibility.find((pe) => {
          return arraysMatch(pe.participantIdPair, pair.individualParticipantsIds);
        });
        if (pairEligibility && !pairEligibility.success)
          return { ...pair, eligibilityError: pairEligibility.error.message };
        else return pair;
      });
      setPairedParticipantsLocal(updatedPairs);
      const hasIneligibilities = updatedPairs?.some((pair) => pair.eligibilityError);
      if (!hasIneligibilities) handleOnSaveDoublesTeams();
    }
  };

  const handleOnSaveDoublesTeams = () => {
    const participantIdPairs = pairedParticipantsLocal.map(
      (pairedParticipant) => pairedParticipant.individualParticipantsIds,
    );
    saveTeams(participantIdPairs);
    closeDialog();
  };

  const handleLocalEligibilities = () => {
    let hasIneligibilities = false;
    const existingPairsIds = pairedDrawParticipants?.map((participant) =>
      participant.pair.map(({ participantId }) => participantId).sort((a, b) => a.localeCompare(b)),
    );
    const updatedPairs = pairedParticipantsLocal.map((localPair) => {
      const localPairIds = [...localPair.individualParticipantsIds].sort((a, b) => a.localeCompare(b));
      if (existingPairsIds.some((existing) => arraysMatch(existing, localPairIds))) {
        hasIneligibilities = true;
        return { ...localPair, eligibilityError: 'pairAlreadyExists' };
      }
      return localPair;
    });
    setPairedParticipantsLocal(updatedPairs);
    if (!hasIneligibilities) handleOnSaveDoublesTeams();
  };

  return (
    <CreateDoublesTeamsOverlay
      closeDialog={closeDialog}
      combinedEligibilityLoading={combinedEligibilityLoading}
      getCombinedEligibility={handleGetCombinedEligibility}
      isOpen={isOpen}
      pairedIndividualParticipants={pairedIndividualParticipants}
      pairedParticipantsLocal={pairedParticipantsLocal}
      setPairedParticipantsLocal={setPairedParticipantsLocal}
      unpairedDrawParticipants={unpairedDrawParticipants}
    />
  );
};

export default CreateDoubleTeamsContainer;
